import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../components/ViewContainer";
import DashboardGrid from "../../components/grid/DashboardGrid";
import Panel from "../../components/grid/Panel";
import KpiStat from "../../components/kpi/KpiStat";
import BarList from "../../components/charts/BarList";
import PanelFootnote from "../../components/PanelFootnote";
import CountUp from "../../components/CountUp";
import { formatInt } from "../../utils/format";
import { brand } from "../../themes/theme";
import { resolveByType } from "../../tenant/tenant";
import dataFlowData from "../../data/dataFlow.json";

// Color por tipo de fuente — mismo criterio que "Sources" en pharma (Calidad
// del dato / Subgroups > Characterization): estructurado/cuestionario en gris
// neutro (la base), activo (autorreporte del paciente) en azul, pasivo (app/
// sensores) en naranja.
const SOURCE_TYPE_COLORS = {
    administrativo: { bg: brand.gray100, border: brand.gray200, color: brand.gray600 },
    autorreporte: { bg: "rgba(50,72,202,0.08)", border: "rgba(50,72,202,0.28)", color: brand.primary },
    pasivo: { bg: "rgba(255,130,57,0.09)", border: "rgba(255,130,57,0.32)", color: brand.accentMedium },
};

// Mismos 3 tipos, version mas clara para relleno de barra (igual que en
// pharma Data Quality): gris intermedio, primaryLight, accentLight.
const COMPLETENESS_BAR_COLORS = {
    administrativo: "#9CA3AF",
    autorreporte: brand.primaryLight,
    pasivo: brand.accentLight,
};

/**
 * HU-3 — Datos y cohorte. Reestructurado al estilo del modulo "Calidad del
 * dato" de pharma (KPIs + paneles de Panel/DashboardGrid) en vez del layout a
 * medida anterior (Origen -> Ordenacion -> Resultado con conectores): misma
 * informacion, misma coherencia visual con el resto del panel.
 */
function DataFlowView() {
    const { t } = useTranslation();
    const data = resolveByType(dataFlowData);

    const completenessItems = data.markers.map((m) => ({
        label: t(`views.dataFlow.markers.${m.key}`),
        pct: m.pct,
        value: `${m.pct}%`,
        color: COMPLETENESS_BAR_COLORS[m.type],
    }));

    return (
        <ViewContainer title={t("views.dataFlow.title")} description={t("views.dataFlow.description")}>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: { xs: "visible", md: "auto" } }}>
            <DashboardGrid gap={2} rowGap={2.5}>
                <Panel span={12} dense>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2, sm: 3 }, flexWrap: "wrap" }}>
                        <Kpi first label={t("views.dataFlow.kpi.cohortLabel")} value={<CountUp to={data.cohorteSize} format={formatInt} />} hint={t("views.dataFlow.kpi.cohortHint")} />
                        <Kpi tone="primary" label={t("views.dataFlow.kpi.markersLabel")}
                            value={<CountUp to={data.markersTotal} format={formatInt} suffix="+" />}
                            hint={t("views.dataFlow.kpi.markersHint", { types: formatInt(data.markerTypes) })} />
                        <Kpi label={t("views.dataFlow.kpi.markerTypesLabel")} value={data.categories} hint={t("views.dataFlow.kpi.markerTypesHint")} />
                        <Kpi label={t("views.dataFlow.kpi.sourcesLabel")} value={data.sources} hint={t("views.dataFlow.kpi.sourcesHint")} />
                        <Kpi label={t("views.dataFlow.kpi.periodLabel")} value={`${data.periodMonths} m`} hint={t("views.dataFlow.kpi.periodHint", { months: data.periodMonths })} />
                    </Box>
                </Panel>

                <Panel span={{ xs: 12, md: 5 }} dense title={t("views.dataFlow.originTitle")} subtitle={t("views.dataFlow.originHint")}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
                        {data.before.map((b) => (
                            <Box
                                key={b.key}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    gap: 1,
                                    px: 1,
                                    py: 0.6,
                                    borderRadius: "6px",
                                    border: "1px solid",
                                    borderColor: brand.gray100,
                                    minWidth: 0,
                                }}
                            >
                                <Box component="span" sx={{ fontSize: 11, color: "text.secondary", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {t(`views.dataFlow.before.${b.key}`)}
                                </Box>
                                <Box component="span" sx={{ fontSize: 12.5, fontWeight: 500, color: "text.primary", flexShrink: 0 }}>
                                    {formatInt(b.value)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Panel>

                <Panel span={{ xs: 12, md: 7 }} dense title={t("views.dataFlow.completenessTitle")} subtitle={t("views.dataFlow.completenessHint")}>
                    <BarList items={completenessItems} />
                </Panel>

                <Panel span={12} dense title={t("views.dataFlow.sourcesTitle")}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {["administrativo", "autorreporte", "pasivo"].map((type) => {
                            const items = [
                                ...data.before.filter((b) => b.type === type).map((b) => ({ key: b.key, label: t(`views.dataFlow.before.${b.key}`) })),
                                ...data.instruments.filter((i) => i.type === type).map((i) => ({ key: i.name, label: i.name })),
                            ];
                            if (!items.length) return null;
                            const c = SOURCE_TYPE_COLORS[type];
                            return (
                                <Box key={type} sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {items.map((item) => (
                                        <Box
                                            key={item.key}
                                            sx={{
                                                fontSize: 11.5,
                                                bgcolor: c.bg,
                                                border: "1px solid",
                                                borderColor: c.border,
                                                color: c.color,
                                                borderRadius: "6px",
                                                px: 1.1,
                                                py: 0.4,
                                            }}
                                        >
                                            {item.label}
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })}
                    </Box>
                    <PanelFootnote>{t("views.dataFlow.sourcesFootnote")}</PanelFootnote>
                </Panel>
            </DashboardGrid>
            </Box>
        </ViewContainer>
    );
}

function Kpi({ label, value, hint, tone, first }) {
    return (
        <Box sx={{ flex: { xs: "0 1 auto", sm: "1 1 150px" }, ...(!first && { borderLeft: { sm: "1px solid" }, borderColor: { sm: "divider" }, pl: { sm: 3 } }) }}>
            <KpiStat label={label} value={value} tone={tone} hint={hint} compact />
        </Box>
    );
}

export default DataFlowView;
